import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAllBusinessHours,
  updateBusinessHours,
  getSpecialClosedDays,
  addSpecialClosedDay,
  removeSpecialClosedDay,
} from "../../../../../../api/configuration";
import {
  DAYS,
  scheduleDraft,
  schedulePayload,
} from "../../../../../../components/scheduleEditor/weeklySchedule";
import { SaveFooter, LoadState, useSavedForm } from "./savedForm";

export const GeneralSchedule = () => {
  const [hours, setHours] = useState(null);
  const [selected, setSelected] = useState("");
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef(false);
  const load = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    setError(false);
    try {
      const response = await getAllBusinessHours();
      if (response?.error !== false || !Array.isArray(response.data))
        throw new Error();
      setHours(response.data);
      const defaults = response.data.filter((h) => h.type === "DEFAULT");
      setSelected((previous) =>
        response.data.some((h) => h.businessHoursId === previous)
          ? previous
          : defaults.length === 1
            ? defaults[0].businessHoursId
            : "",
      );
    } catch {
      setError(true);
    } finally {
      pending.current = false;
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="generalReferenceForm">
      <div className="generalScheduleSelector">
        {error ? (
          <div>
            <p role="alert">No se han podido cargar los horarios.</p>
            <button type="button" onClick={load}>
              Reintentar horarios
            </button>
          </div>
        ) : hours === null ? (
          <p role="status">Cargando horarios…</p>
        ) : (
          <label>
            Tipo de horario
            <select
              disabled={locked}
              aria-label="Horario de referencia"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Seleccionar horario</option>
              {hours.map((hour) => (
                <option key={hour.businessHoursId} value={hour.businessHoursId}>
                  {hour.name}
                  {hour.type === "DEFAULT" ? " · Principal" : ""}
                </option>
              ))}
            </select>
            <small>
              Selecciona un horario existente. Su tipo y sus canales asociados
              se conservan.
            </small>
          </label>
        )}
        {hours && !selected && (
          <p>
            No hay un horario general seleccionado. Puedes elegir uno existente
            o gestionarlos desde Horarios.
          </p>
        )}
      </div>
      {selected && (
        <WeeklyForm key={selected} id={selected} onBusyChange={setLocked} />
      )}
      <SpecialDates />
    </div>
  );
};
const weeklyPayload = (draft) => {
  const { salesChannelId: _salesChannelId, ...payload } = schedulePayload({
    ...draft,
    lastOrderTime: draft.lastOrderTime === "" ? 30 : draft.lastOrderTime,
  });
  payload.dayInfo = { ...draft.dayInfo, ...payload.dayInfo };
  if (draft.lastOrderTime === "") payload.lastOrderTime = null;
  return payload;
};
const sameWeekly = (left, right) => {
  try {
    return (
      JSON.stringify(weeklyPayload(left)) ===
      JSON.stringify(weeklyPayload(right))
    );
  } catch {
    return false;
  }
};
export const WeeklyForm = ({ id, onBusyChange }) => {
  const read = useCallback(async () => {
    const response = await getAllBusinessHours();
    const raw = response?.data?.find?.((h) => h.businessHoursId === id);
    if (response?.error !== false || !raw) throw new Error();
    return {
      raw,
      draft: {
        ...scheduleDraft(raw),
        dayInfo: { ...raw.dayInfo, ...scheduleDraft(raw).dayInfo },
        lastOrderTime: raw.lastOrderTime ?? "",
      },
    };
  }, [id]);
  const write = useCallback(
    (draft) => {
      const payload = weeklyPayload(draft);
      return updateBusinessHours(payload, id);
    },
    [id],
  );
  const form = useSavedForm(read, write, sameWeekly);
  useEffect(() => {
    onBusyChange?.(Boolean(form.dirty || form.saving));
  }, [onBusyChange, form.dirty, form.saving]);
  if (!form.record) return <LoadState form={form} label="horario semanal" />;
  const change = (key, value) =>
    form.setDraft((previous) => ({ ...previous, [key]: value }));
  const changeDay = (day, key, value) =>
    form.setDraft((previous) => ({
      ...previous,
      dayInfo: {
        ...previous.dayInfo,
        [day]: { ...previous.dayInfo[day], [key]: value },
      },
    }));
  return (
    <form onSubmit={form.save} aria-busy={form.saving}>
      <fieldset disabled={form.saving}>
        <legend className="visually-hidden">Horario semanal</legend>
        <div className="generalFormBody weeklyControls">
          <label>
            Tiempo del último pedido
            <input
              type="number"
              min="1"
              max="60"
              placeholder="Sin configurar"
              value={form.draft.lastOrderTime}
              onChange={(e) => change("lastOrderTime", e.target.value)}
            />
            <small>
              Tiempo límite antes del cierre para aceptar el último pedido.
            </small>
          </label>
          <label className="maintenanceSwitch" htmlFor="general-continuous">
            <span>
              <b>Horario continuo</b>
              <small>Cada día abre en una sola franja horaria.</small>
            </span>
            <input
              id="general-continuous"
              aria-label="Horario continuo"
              type="checkbox"
              role="switch"
              checked={form.draft.isContinuous}
              onChange={(e) => change("isContinuous", e.target.checked)}
            />
          </label>
        </div>
        <div className="weeklyTable">
          <div className="weeklyHeader">
            <span>DÍA</span>
            <span>HORARIO</span>
            <span>CERRADO</span>
          </div>
          {DAYS.map(([day, label]) => (
            <div className="weeklyRow" key={day}>
              <b>{label}</b>
              <div className="weeklyTimes">
                {(form.draft.isContinuous
                  ? [
                      ["openingTime", "Apertura"],
                      ["closingTime", "Cierre"],
                    ]
                  : [
                      ["openingTime", "Apertura"],
                      ["splitClosingTime", "Cierre de mañana"],
                      ["splitOpeningTime", "Reapertura"],
                      ["closingTime", "Cierre final"],
                    ]
                ).map(([field, name]) => (
                  <label key={field}>
                    <span className="visually-hidden">
                      {label}: {name}
                    </span>
                    <input
                      aria-label={`${label}: ${name}`}
                      type="time"
                      step="60"
                      required={!form.draft.dayInfo[day].isClosed}
                      disabled={form.draft.dayInfo[day].isClosed}
                      value={form.draft.dayInfo[day][field]}
                      onChange={(e) => changeDay(day, field, e.target.value)}
                    />
                  </label>
                ))}
              </div>
              <label className="generalCheckbox">
                <input
                  type="checkbox"
                  aria-label={`${label}: cerrado`}
                  checked={form.draft.dayInfo[day].isClosed}
                  onChange={(e) => changeDay(day, "isClosed", e.target.checked)}
                />
                Cerrado
              </label>
            </div>
          ))}
        </div>
        <p className="weeklyHelp">
          Marca «Cerrado» en los días en los que el negocio no abre; las horas
          se conservan para cuando vuelva a abrir.
        </p>
        <SaveFooter {...form} />
      </fieldset>
    </form>
  );
};

export const SpecialDates = () => {
  const [days, setDays] = useState(null),
    [date, setDate] = useState(""),
    [adding, setAdding] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [removing, setRemoving] = useState(null);
  const pending = useRef(false);
  const read = useCallback(async () => {
    const response = await getSpecialClosedDays();
    if (response?.error !== false || !Array.isArray(response.data))
      throw new Error();
    return response.data;
  }, []);
  const load = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    setError("");
    try {
      setDays(await read());
    } catch {
      setError("No se han podido cargar las fechas especiales.");
    } finally {
      pending.current = false;
    }
  }, [read]);
  useEffect(() => {
    load();
  }, [load]);
  const mutate = async (event, id) => {
    event?.preventDefault();
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const current = await read();
      // Check current state on each retry: do not duplicate a date after a lost response.
      const exists = id
        ? current.some((day) => day.specialClosedDaysId === id)
        : current.some((day) => day.date === date);
      if (id ? exists : !exists) {
        const response = id
          ? await removeSpecialClosedDay({}, id)
          : await addSpecialClosedDay({ date });
        if (response?.error !== false) throw new Error();
      }
      const confirmed = await read();
      if (
        id
          ? confirmed.some((day) => day.specialClosedDaysId === id)
          : !confirmed.some((day) => day.date === date)
      )
        throw new Error();
      setDays(confirmed);
      setAdding(false);
      setDate("");
      setRemoving(null);
    } catch {
      setError(
        "No se ha podido confirmar el cambio de fecha. Reintenta para consultar su estado actual.",
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <section className="specialDates">
      <div className="specialDatesHeading">
        <div>
          <h3>Horario especial</h3>
          <p>Fechas en las que el negocio permanece cerrado todo el día.</p>
        </div>
        <button
          type="button"
          disabled={busy || days === null}
          onClick={() => setAdding(true)}
        >
          ＋ Agregar fecha
        </button>
      </div>
      <p>
        Los horarios parciales para fechas especiales y una regla semanal de
        festivos no están disponibles en la configuración actual.
      </p>
      {days === null && !error && (
        <p role="status">Cargando fechas especiales…</p>
      )}
      {error && (
        <div>
          <p role="alert">{error}</p>
          {days === null && (
            <button type="button" onClick={load}>
              Reintentar fechas especiales
            </button>
          )}
        </div>
      )}
      {days?.length === 0 && <p>No hay fechas especiales definidas.</p>}
      {days?.map((day) => (
        <div className="specialDateRow" key={day.specialClosedDaysId}>
          <time dateTime={day.date}>
            {day.date.split("-").reverse().join("/")}
          </time>
          <span>Cerrado todo el día</span>
          {removing === day.specialClosedDaysId ? (
            <div>
              <button
                type="button"
                disabled={busy}
                onClick={(event) => mutate(event, day.specialClosedDaysId)}
              >
                Confirmar eliminación
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setRemoving(null)}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={busy}
              aria-label={`Eliminar fecha ${day.date}`}
              onClick={() => setRemoving(day.specialClosedDaysId)}
            >
              Eliminar
            </button>
          )}
        </div>
      ))}
      {adding && (
        <form onSubmit={(event) => mutate(event)}>
          <fieldset disabled={busy}>
            <label>
              Fecha de cierre
              <input
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <span>Cerrado todo el día</span>
            <button type="submit">
              {busy ? "Guardando…" : "Guardar fecha"}
            </button>
            <button type="button" onClick={() => setAdding(false)}>
              Cancelar
            </button>
          </fieldset>
        </form>
      )}
    </section>
  );
};
